/**
 * A client's Billing area as staff read it on its page (docs/28 §11): spend
 * by day and service, and a month's statement — what each service took from
 * its prepaid balance, line by line. Staff see what the client never does:
 * whether the statement balanced against the ledger, and the AI models a
 * line's calls were answered by.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Download } from 'lucide-react';
import {
  Alert,
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  QueryPending,
  Select,
  api,
  formatMoney,
  formatNumber,
  toast,
} from '@connect/web-shared';

import { formatMetered } from './ai-card';

type UsageService = 'ai' | 'email' | 'rcs' | 'sms' | 'push';

const LABELS: Record<UsageService, string> = {
  ai: 'AI assistant',
  email: 'Email',
  rcs: 'RCS',
  sms: 'SMS credits',
  push: 'Push notifications',
};
const COLOURS: Record<UsageService, string> = {
  ai: 'var(--color-accent)',
  email: 'var(--color-success)',
  rcs: 'var(--color-warning)',
  sms: 'var(--color-info)',
  push: 'var(--color-muted)',
};

interface Overview {
  so_far: string;
  last_month_to_date: string;
  last_month_total: string;
  forecast: string | null;
  services: UsageService[];
  days: { day: string; services: Partial<Record<UsageService, string>> }[];
}

interface StaffStatement {
  month: string;
  so_far: boolean;
  opening: string;
  money_in: string;
  adjustments: string;
  taken: string;
  closing: string;
  balanced: boolean;
  computed_closing: string;
  lines: {
    service: UsageService;
    what: string;
    label: string;
    quantity: string;
    rate: string | null;
    amount: string;
    tokens_in?: number;
    tokens_out?: number;
    models?: string[];
  }[];
}

function monthLabel(month: string): string {
  const [year, number] = month.split('-').map(Number);
  return new Date(year!, (number ?? 1) - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function ClientBillingArea({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const base = `/billing/clients/${clientPublicId}/area`;
  const overview = useQuery({ queryKey: ['billing-area', clientPublicId, 'overview'], queryFn: () => api.get<Overview>(`${base}/overview`) });
  const months = useQuery({ queryKey: ['billing-area', clientPublicId, 'months'], queryFn: () => api.get<string[]>(`${base}/statements`) });
  const [chosen, setChosen] = useState<string | null>(null);
  const month = chosen ?? months.data?.[0] ?? null;
  const statement = useQuery({
    queryKey: ['billing-area', clientPublicId, 'statement', month],
    queryFn: () => api.get<StaffStatement>(`${base}/statements/${month}`),
    enabled: month !== null,
  });
  const download = useMutation({
    mutationFn: () => api.download(`${base}/statements/${month}/csv`, `${clientName}-statement-${month}.csv`),
    onError: (error) => toast.error('Not downloaded', error instanceof ApiError ? error.message : undefined),
  });

  // The chart's bars are drawn from the server's figures; nothing is summed
  // here that a person reads as an amount.
  const chartData = (overview.data?.days ?? []).map((d) =>
    Object.assign(
      { day: d.day.slice(5) },
      Object.fromEntries((overview.data?.services ?? []).map((s) => [s, Number(d.services[s] ?? 0)])),
    ),
  );
  const s = statement.data;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Spend, last 30 days"
          description={
            overview.data
              ? `This month so far ${formatMoney(overview.data.so_far, 'INR')} · last month to the same point ${formatMoney(overview.data.last_month_to_date, 'INR')} · month-end at this pace ${overview.data.forecast ? formatMoney(overview.data.forecast, 'INR') : '—'}`
              : undefined
          }
        />
        <CardBody>
          {overview.data === undefined ? (
          <QueryPending query={overview} label="Loading spend" />
        ) : chartData.length === 0 || (overview.data?.services.length ?? 0) === 0 ? (
            <p className="text-sm text-muted">Nothing taken in the last 30 days.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="day" fontSize={11} tickLine={false} />
                  <YAxis fontSize={11} tickLine={false} width={48} />
                  <Tooltip />
                  <Legend formatter={(value: string) => LABELS[value as UsageService] ?? value} />
                  {(overview.data?.services ?? []).map((service) => (
                    <Bar key={service} dataKey={service} stackId="spend" fill={COLOURS[service]} name={service} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Statement"
          description="What each service took from the prepaid balance in a month — the client sees the same, without the models."
          actions={
            <div className="flex items-end gap-2">
              <div className="w-48">
                <Select
                  label="Month"
                  options={(months.data ?? []).map((m) => ({ value: m, label: monthLabel(m) }))}
                  value={month ?? ''}
                  onChange={(event) => setChosen(event.target.value)}
                />
              </div>
              <Button variant="secondary" size="sm" disabled={!s} loading={download.isPending} onClick={() => download.mutate()}>
                <Download className="size-4" />
                CSV
              </Button>
            </div>
          }
        />
        <CardBody className="space-y-3">
          {months.data?.length === 0 ? (
            <EmptyState
              title="No statements yet"
              description="A month appears here once something is charged to the prepaid balance."
            />
          ) : s === undefined ? (
            // Until a month is known the statement is not asked for, so what
            // stands in for it is the months' own state.
            <QueryPending query={month === null ? months : statement} label="Loading the statement" />
          ) : (
            <>
              {!s.balanced ? (
                <Alert tone="danger" title="This statement does not add up to the ledger">
                  Worked out {formatMetered(s.computed_closing)}, the ledger says {formatMetered(s.closing)}. Report it — the
                  ledger is the truth.
                </Alert>
              ) : null}
              <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
                {[
                  ['Opening', s.opening],
                  ['Money in', s.money_in],
                  ['Adjustments', s.adjustments],
                  ['Taken', s.taken],
                  [s.so_far ? 'Balance now' : 'Closing', s.closing],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md bg-surface-2 p-2">
                    <dt className="text-2xs uppercase tracking-wide text-muted">{label}</dt>
                    <dd className="font-mono tabular text-heading">{formatMetered(value!)}</dd>
                  </div>
                ))}
              </dl>
              {s.lines.length === 0 ? (
                <p className="text-sm text-muted">No service took anything this month.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left text-muted">
                    <tr>
                      <th className="py-1">Service</th>
                      <th className="py-1">What</th>
                      <th className="py-1 text-right">Quantity</th>
                      <th className="py-1 text-right">Rate</th>
                      <th className="py-1 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((line) => (
                      <tr key={`${line.service}-${line.what}-${line.rate}`} className="border-t border-line">
                        <td className="py-1.5">{LABELS[line.service] ?? line.service}</td>
                        <td className="py-1.5">
                          {line.label}
                          {line.tokens_in !== undefined ? (
                            <span className="block text-muted">
                              {formatNumber(line.tokens_in)} in · {formatNumber(line.tokens_out ?? 0)} out
                              {line.models?.length ? ` · ${line.models.join(', ')}` : ''}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-1.5 text-right font-mono tabular">{formatNumber(Number(line.quantity))}</td>
                        <td className="py-1.5 text-right font-mono tabular">{line.rate ? formatMetered(line.rate) : '—'}</td>
                        <td className="py-1.5 text-right font-mono tabular">{formatMetered(line.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
